import React, { useMemo, useState } from 'react';
import { addDays, addWeeks, isSameDay, startOfWeek } from 'date-fns';
import { CalendarCheck, ChevronLeft, ChevronRight, Copy, Lock, Plus, RotateCcw, Send, Undo2 } from 'lucide-react';

import PageCard from '../../PageCard/PageCard';
import { Button } from '../../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { cn } from '../../../lib/utils';
import { Timesheet, TimesheetRow, WEEK_TARGET_HOURS } from '../../../model/timesheet.model';
import { availableRows, emptySheet, initialSheets } from './mock';
import WeekGrid from './components/WeekGrid';
import { formatTotal } from './components/HourCell';

const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const weekLabel = (start: Date, end: Date) =>
  start.getMonth() === end.getMonth()
    ? `${MONTHS_FULL[start.getMonth()]} ${start.getDate()}–${end.getDate()}`
    : `${MONTHS_SHORT[start.getMonth()]} ${start.getDate()} – ${MONTHS_SHORT[end.getMonth()]} ${end.getDate()}`;

const weekCaption = (offset: number, start: Date) => {
  if (offset === 0) return 'Current week';
  if (offset === -1) return 'Last week';
  if (offset === 1) return 'Next week';
  return String(start.getFullYear());
};

const sumRows = (rows: TimesheetRow[], predicate: (row: TimesheetRow) => boolean = () => true) =>
  rows
    .filter(predicate)
    .reduce((acc, row) => acc + row.hours.reduce((rowAcc, value) => rowAcc + value, 0), 0);

const TimesheetPage: React.FC = () => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [sheets, setSheets] = useState<Record<number, Timesheet>>(() => initialSheets());

  const sheet = sheets[weekOffset] ?? emptySheet();
  const locked = sheet.status === 'submitted' || sheet.status === 'approved';

  const days = useMemo(() => {
    const monday = startOfWeek(addWeeks(new Date(), weekOffset), { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, index) => addDays(monday, index));
  }, [weekOffset]);

  const todayIndex = days.findIndex(day => isSameDay(day, new Date()));

  const total = sumRows(sheet.rows);
  const billable = sumRows(sheet.rows, row => row.billable);
  const nonBillable = total - billable;

  const usedIds = sheet.rows.map(row => row.id);
  const addable = availableRows.filter(row => !usedIds.includes(row.id));

  const patchSheet = (patch: (current: Timesheet) => Timesheet) =>
    setSheets(prev => ({ ...prev, [weekOffset]: patch(prev[weekOffset] ?? emptySheet()) }));

  const handleChangeHours = (rowId: string, day: number, value: number) =>
    patchSheet(current => ({
      ...current,
      rows: current.rows.map(row =>
        row.id === rowId
          ? { ...row, hours: row.hours.map((hour, index) => (index === day ? value : hour)) }
          : row,
      ),
    }));

  const handleRemoveRow = (rowId: string) =>
    patchSheet(current => ({ ...current, rows: current.rows.filter(row => row.id !== rowId) }));

  const handleAddRow = (id: string) => {
    const template = availableRows.find(row => row.id === id);
    if (!template) return;
    patchSheet(current => ({
      ...current,
      rows: [...current.rows, { ...template, hours: [0, 0, 0, 0, 0, 0, 0] }],
    }));
  };

  const handleCopyPreviousWeek = () => {
    const previous = sheets[weekOffset - 1];
    if (!previous) return;
    patchSheet(current => ({
      ...current,
      rows: current.rows.map(row => {
        const source = previous.rows.find(candidate => candidate.id === row.id);
        return source ? { ...row, hours: [...source.hours] } : row;
      }),
    }));
  };

  const handleSubmit = () => patchSheet(current => ({ ...current, status: 'submitted' }));
  const handleWithdraw = () => patchSheet(current => ({ ...current, status: 'draft' }));

  const weekNavigator = (
    <div className="flex items-center gap-2">
      <div className="flex items-center rounded-md border border-border">
        <button
          type="button"
          onClick={() => setWeekOffset(offset => offset - 1)}
          className="rounded-l-md px-2 py-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title="Previous week"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => setWeekOffset(offset => offset + 1)}
          className="rounded-r-md border-l border-border px-2 py-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          title="Next week"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div>
        <div className="text-base font-medium leading-tight">{weekLabel(days[0], days[6])}</div>
        <div className="text-xs text-muted-foreground">{weekCaption(weekOffset, days[0])}</div>
      </div>

      {weekOffset !== 0 && (
        <Button variant="ghost" size="sm" onClick={() => setWeekOffset(0)}>
          Today
        </Button>
      )}
    </div>
  );

  const actions = (
    <div className="flex items-center gap-2">
      {!locked && sheets[weekOffset - 1] && (
        <Button variant="default" size="sm" onClick={handleCopyPreviousWeek}>
          <Copy />
          Copy last week
        </Button>
      )}
      {!locked && (
        // The shared `primary` variant hardcodes text-white, which is unreadable on the
        // light --primary of the dark theme.
        <Button
          variant="primary"
          size="sm"
          className="dark:text-primary-foreground"
          disabled={total === 0}
          onClick={handleSubmit}
        >
          <Send />
          Submit for approval
        </Button>
      )}
      {sheet.status === 'submitted' && (
        <Button variant="default" size="sm" onClick={handleWithdraw}>
          <Undo2 />
          Withdraw
        </Button>
      )}
      {sheet.status === 'approved' && (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Lock className="h-3.5 w-3.5" />
          Sheet locked
        </span>
      )}
    </div>
  );

  const billableShare = total > 0 ? Math.round((billable / total) * 100) : 0;
  const barBase = Math.max(total, WEEK_TARGET_HOURS);

  return (
    <PageCard title={weekNavigator} extra={actions}>
      {sheet.status === 'submitted' && (
        <Banner
          tone="pending"
          icon={<CalendarCheck className="h-4 w-4" />}
          title="Submitted for approval"
          text={`Waiting on ${sheet.reviewer}. Hours can't be changed while the sheet is under review.`}
        />
      )}
      {sheet.status === 'approved' && (
        <Banner
          tone="approved"
          icon={<Lock className="h-4 w-4" />}
          title="Approved"
          text={`${sheet.reviewer}, ${sheet.reviewedOn}. Entries are locked and have gone into the client invoice.`}
        />
      )}
      {sheet.status === 'rejected' && (
        <Banner
          tone="rejected"
          icon={<RotateCcw className="h-4 w-4" />}
          title="Sent back"
          text={sheet.rejectionNote ?? ''}
        />
      )}

      {/* Summary strip — the only number that matters while filling the week. */}
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-semibold tabular-nums leading-none">{formatTotal(total)}</span>
          <span className="text-sm text-muted-foreground">of {WEEK_TARGET_HOURS} h</span>
          {total > WEEK_TARGET_HOURS && (
            <span className="text-xs text-amber-600 dark:text-amber-500">
              +{formatTotal(total - WEEK_TARGET_HOURS)} over target
            </span>
          )}
        </div>

        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-foreground" />
            Billable {formatTotal(billable)} h · {billableShare}%
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-muted-foreground/40" />
            Non-billable {formatTotal(nonBillable)} h
          </span>
        </div>
      </div>

      <div className="mb-6 flex h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="bg-foreground transition-all duration-300"
          style={{ width: `${(billable / barBase) * 100}%` }}
        />
        <div
          className="bg-muted-foreground/40 transition-all duration-300"
          style={{ width: `${(nonBillable / barBase) * 100}%` }}
        />
      </div>

      <WeekGrid
        rows={sheet.rows}
        days={days}
        todayIndex={todayIndex}
        locked={locked}
        onChangeHours={handleChangeHours}
        onRemoveRow={handleRemoveRow}
        addProjectSlot={
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={addable.length === 0}
                className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
              >
                <Plus className="h-3.5 w-3.5" />
                Add project
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 p-1">
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                Projects your organization assigned to you
              </div>
              <div className="max-h-64 overflow-y-auto">
                {addable.map(row => (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => handleAddRow(row.id)}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted"
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-sm"
                      style={{ backgroundColor: row.projectColor }}
                    />
                    <span className="truncate">
                      <span className="text-muted-foreground">{row.clientName}</span>
                      <span className="mx-1 text-muted-foreground/50">·</span>
                      {row.projectName}
                      <span className="mx-1 text-muted-foreground/50">·</span>
                      <span className="text-foreground/90">{row.workType}</span>
                    </span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        }
      />

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <Hint keys="↑ ↓ ← →" label="move" />
          <Hint keys="Enter" label="next row" />
          <Hint keys="3:30" label="or 3.5" />
          <Hint keys="Esc" label="cancel" />
        </div>
        <span>Personal categories from the app never reach the timesheet, and your employer cannot see them.</span>
      </div>
    </PageCard>
  );
};

const Hint: React.FC<{ keys: string; label: string }> = ({ keys, label }) => (
  <span className="flex items-center gap-1.5">
    <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-foreground/70">
      {keys}
    </kbd>
    {label}
  </span>
);

const Banner: React.FC<{
  tone: 'pending' | 'approved' | 'rejected';
  icon: React.ReactNode;
  title: string;
  text: string;
}> = ({ tone, icon, title, text }) => (
  <div
    className={cn(
      'mb-4 flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-sm',
      tone === 'pending' && 'border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200',
      tone === 'approved' && 'border-emerald-500/30 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200',
      tone === 'rejected' && 'border-destructive/30 bg-destructive/10 text-destructive',
    )}
  >
    <span className="mt-0.5 shrink-0">{icon}</span>
    <span>
      <span className="font-medium">{title}</span>
      <span className="mx-1.5 opacity-40">·</span>
      <span className="opacity-90">{text}</span>
    </span>
  </div>
);

export default TimesheetPage;
